import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { App } from "antd";
import axios from "axios";
import { useSearch } from "../../../../contexts/SearchContext";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import { useAppContext, round2 } from "../../../../contexts/AppContext";
import { getUserDetails, isAdminUser } from "../../../../services/authService";
import { sendFlightProposalToSupplier } from "../../../../services/flightProposalApi";
import { bookFlightOnly } from "../../../../services/flightService";
import { updateLdsOrder } from "../../../../services/fhpService";
import {
  getDynamicOrder,
  postFlightsNewProposalBankTransfer,
} from "../../../../services/dynamicService";
import {
  get_depret_from_result,
  parseToLdsDate,
} from "../../../../utils/LDSHelpers";
import { buildLdsOrderPersistCommissionFields } from "../../../../utils/ldsOrderPersistCommission";
import { isFlightsNewProposalPackage } from "../../../../utils/dynamicPackageDisplayMode";
import {
  assertFlightPassengersHaveNames,
  buildFlightPassengersInfoOnlyUpdatePayload,
  existingPassengersInfoFromOrderRow,
  flightPassengersInfoNeedsSync,
  isFlightPassengerNamesIncompleteError,
  logFlightPassengerSyncTrace,
  resolveBankTransferFailureToast,
  resolveExistingLdsOrderIdFromDynamicRow,
  resolveIncomingPassengersForPayment,
  resolveFlightPaymentPersistAction,
  summarizeFlightPassengersForTrace,
  uiPassengersHaveAnyName,
  uiPassengersHaveCompleteNames,
} from "../../../../utils/flights/flightBookingPassengerSync";
import {
  shouldRefreshDraftBeforeBankTransfer,
  shouldRetryBankTransferAfterFailure,
} from "../../../../utils/flights/flightBankTransferFirstSubmit";
import { mergeBookingFlowPassengersIntoRoomsAndPaxInfo } from "../../adapters/flightOfferAdapter";
import {
  buildFlightsNewLegFlightsInfo,
  resolveSelfComposeLdsSaveDeals,
} from "../../adapters/selfComposeLdsSaveLegs";
import { buildLdsSupplementObjectForProposalSave } from "../../adapters/buildLdsSupplementObjectForProposalSave";
import { useBookingFlow } from "../hooks/useBookingFlow";
import { USE_REAL_LDS_PROPOSAL_FROM_FLIGHT_MOCKUP_REVIEW } from "../../../../config/flightBookingMockupSearchOffer";
import { applyLdsHandlingFeeAndCardToFareDetails } from "../../../../pricing/vacationPackageLdsAdapter";
import { useAgentCommissionSettings } from "../../../../features/agentEntitlements/useAgentCommissionSettings";
import { buildPublicFlightOrDynamicOfferUrl } from "../../../../utils/publicOfferPaths";
import {
  absolutizePublicFrontendUrl,
  resolvePublicBaseUrl,
} from "../../../../utils/resolvePublicBaseUrl";
import { ApiReqBaseUrl } from "../../../../assets/GlobalVariables";
import { computeSupplementPriceIls } from "../../../../utils/lds/baggageSupplementPricing";
import { resolveLdsManagedPayableBaseIls } from "../../../../utils/orders/ldsMissingHandlingFeePayableUplift";

const FlightRealProposalSubmitContext = createContext(null);

function computeCheckedSupplementsTotalIlsFromPayload(supplementPayload) {
  if (!supplementPayload || typeof supplementPayload !== "object") return 0;
  const entries = Array.isArray(supplementPayload)
    ? supplementPayload
    : Object.values(supplementPayload);
  return entries.reduce((sum, entry) => {
    if (!entry || typeof entry !== "object" || entry.checked !== true) return sum;
    const n = Number(entry.ourFare ?? entry.fare ?? 0);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function clientsInfoFromFlightLead(passengers, mergedRooms) {
  const lead =
    (Array.isArray(passengers) && passengers[0] && typeof passengers[0] === "object"
      ? passengers[0]
      : null) ||
    mergedRooms?.[0]?.adults?.[0] ||
    {};
  const firstName = String(lead.firstName ?? lead.firstname ?? "").trim();
  const lastName = String(lead.lastName ?? lead.lastname ?? "").trim();
  const email = String(lead.email ?? "").trim();
  const phone = String(lead.cell ?? lead.phone ?? "").trim();
  const fullname = [firstName, lastName].filter(Boolean).join(" ");
  if (!fullname && !email && !phone) return {};
  return { firstName, lastName, email, phone, fullname };
}

function embedBaggageIntoOurPriceForLds({
  baseOurPrice,
  checkedSupplementsTotal,
  finalCustomerTotal,
}) {
  if (!baseOurPrice || typeof baseOurPrice !== "object") return baseOurPrice;
  const baggageTotal = Number(checkedSupplementsTotal || 0);
  const finalTotal = Number(finalCustomerTotal || 0);
  const hasBaggage = Number.isFinite(baggageTotal) && baggageTotal > 0;
  const hasFinal = Number.isFinite(finalTotal) && finalTotal > 0;
  return {
    ...baseOurPrice,
    ...(hasBaggage
      ? {
          baggagePrice: round2(baggageTotal),
          public_baggage_total_ils: round2(baggageTotal),
        }
      : null),
    ...(hasFinal
      ? {
          display_total_ils: round2(finalTotal),
          customer_total: round2(finalTotal),
          final_customer_total_ils: round2(finalTotal),
        }
      : null),
    baggage_included_in_display_total_ils: hasBaggage,
    baggage_total_source: hasBaggage ? "supplement.checked.ourFare" : null,
    ourTotalFare:
      baseOurPrice?.ourTotalFare && typeof baseOurPrice.ourTotalFare === "object"
        ? {
            ...baseOurPrice.ourTotalFare,
            ...(hasBaggage ? { baggagePrice: round2(baggageTotal) } : null),
            ...(hasFinal ? { display_total_ils: round2(finalTotal) } : null),
          }
        : baseOurPrice?.ourTotalFare,
  };
}

export function FlightRealProposalSubmitProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { currencyRate, getCreditCardFeePercent, getBaggageFeeFlat } =
    useAppContext();
  const {
    savedDeal,
    roomInfo,
    calculateLDSDealFareForRoomsComposition,
  } = useSearch();
  const {
    roomsAndPaxInfo,
    extendedDealApiData,
    dynamicOrder,
    ourSavedLDSOrder,
    setOurSavedLDSOrder,
    setDynamicOrder,
    handleFeeData,
  } = useSelectedResult();

  const {
    selectedFlightOffer,
    passengers,
    extras,
    pricing,
    passengersStepValid,
    currentStep,
    nextStep,
    canAdvanceFromCurrentStep,
  } = useBookingFlow();

  const { notification } = App.useApp();
  const { fixedFlightCommission } = useAgentCommissionSettings();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const draftInflightRef = useRef(null);
  const [lastError, setLastError] = useState(null);
  /** אחרי יצירת הצעת מחיר מטיסות — נשארים באותו מסך; publicOfferUrl = ללקוח */
  const [flightQuoteCreateResult, setFlightQuoteCreateResult] = useState(null);
  const [isSubmittingPayNow, setIsSubmittingPayNow] = useState(false);
  const [isSubmittingBankTransfer, setIsSubmittingBankTransfer] = useState(false);
  const [manualPaymentUrl, setManualPaymentUrl] = useState("");
  const [pendingPaymentOrderId, setPendingPaymentOrderId] = useState(null);

  const onSearchOffer =
    typeof location.pathname === "string" &&
    location.pathname.includes("/searchoffer");

  const useRealFlow =
    USE_REAL_LDS_PROPOSAL_FROM_FLIGHT_MOCKUP_REVIEW && onSearchOffer;

  const applyAgentFeeToOurPrice = useCallback(
    (baseOurPrice, numberOfRooms, options = {}) => {
      if (!baseOurPrice?.ourTotalFare) return baseOurPrice;
      const useManagedTotal = options.useManagedTotal !== false;
      // SSOT קודם: אם המחיר המנוהל כבר חושב בפלואו, משתמשים בו ישירות.
      if (useManagedTotal && pricing?.managedOurTotalFare) {
        return {
          ...baseOurPrice,
          ourTotalFare: {
            ...baseOurPrice.ourTotalFare,
            ...pricing.managedOurTotalFare,
            handleFeeData,
          },
        };
      }
      const { ourFareDetails } = applyLdsHandlingFeeAndCardToFareDetails({
        ourFareDetails: baseOurPrice,
        numberOfRooms: Math.max(1, Number(numberOfRooms) || 1),
        handleFeeData,
        getCreditCardFeePercent,
        fixedFlightCommission,
      });
      const adjusted = ourFareDetails || baseOurPrice;
      if (adjusted?.ourTotalFare) {
        adjusted.ourTotalFare.handleFeeData = handleFeeData;
      }
      return adjusted;
    },
    [
      pricing?.managedOurTotalFare,
      handleFeeData,
      getCreditCardFeePercent,
      fixedFlightCommission,
    ]
  );

  const resolveExistingDynamicOrderId = useCallback(() => {
    const row = dynamicOrder || ourSavedLDSOrder;
    const id = row?.id;
    return id != null && String(id).trim() !== "" ? String(id).trim() : null;
  }, [dynamicOrder, ourSavedLDSOrder]);

  const buildBookFlightOnlyPayload = useCallback(
    ({ agentId, passengersPayload, mergedRoomsAndPaxForSupplements }) => {
      const rawDeal = selectedFlightOffer?.rawDeal ?? savedDeal;
      if (!rawDeal) {
        throw new Error("לא נבחרה טיסה לשמירה.");
      }
      const computeOurFareNis = (fareUsd, supplementRow = null) =>
        computeSupplementPriceIls({
          fareUsd,
          currencyRateUsd: currencyRate.USD,
          baggageFeeFlatUsd: getBaggageFeeFlat(),
          supplement: supplementRow,
        });

      const saveDeals = resolveSelfComposeLdsSaveDeals({
        selectedFlightOffer,
        rawDeal,
      });
      const perLeg = saveDeals.kind === "two_oneway";
      const deals = perLeg
        ? [saveDeals.outbound, saveDeals.inbound]
        : [saveDeals.deal];

      const builtLegs = deals.map((deal) => {
        const basePrice = calculateLDSDealFareForRoomsComposition(deal);
        const ourPrice = applyAgentFeeToOurPrice(basePrice, roomsAndPaxInfo?.length, {
          useManagedTotal: !perLeg,
        });
        if (!ourPrice?.ourTotalFare) {
          throw new Error("לא ניתן לחשב מחיר להזמנה — נסו חיפוש מחדש.");
        }
        const lf = deal.linkedFlights || deal.linkedflights;
        const ldsSupplementPayload = buildLdsSupplementObjectForProposalSave({
          mergedRoomsAndPaxInfo: mergedRoomsAndPaxForSupplements,
          linkedFlights: lf,
          rawSupplements: deal.supplement,
          extras,
          computeOurFareNis,
        });
        const persistComm = buildLdsOrderPersistCommissionFields(ourPrice);
        const flightsInfo = buildFlightsNewLegFlightsInfo({
          agentId,
          persistComm,
          rawDeal: deal,
          linkedFlights: lf,
          passengersPayload,
          ldsSupplementPayload,
          ourPrice,
          extendedDealApiData,
          parseToLdsDate,
          getDepRetFromResult: get_depret_from_result,
        });
        const baseManagedTotal = Number(resolveLdsManagedPayableBaseIls(ourPrice) || 0);
        const checkedSupplementsTotal = computeCheckedSupplementsTotalIlsFromPayload(
          ldsSupplementPayload
        );
        const totalPriceToSave = round2(
          (Number.isFinite(baseManagedTotal) ? baseManagedTotal : 0) +
            (Number.isFinite(checkedSupplementsTotal) ? checkedSupplementsTotal : 0)
        );
        flightsInfo.ourPrice = embedBaggageIntoOurPriceForLds({
          baseOurPrice: ourPrice,
          checkedSupplementsTotal,
          finalCustomerTotal: totalPriceToSave,
        });
        return { flightsInfo, totalPriceToSave };
      });

      const outbound = builtLegs[0];
      if (builtLegs[1]) {
        outbound.flightsInfo.self_compose_inbound = builtLegs[1].flightsInfo;
      }
      const totalPriceToSave = round2(
        builtLegs.reduce((sum, leg) => sum + (Number(leg.totalPriceToSave) || 0), 0)
      );
      const dynRow = dynamicOrder || ourSavedLDSOrder;
      const dynamicId = params.dynamicOrderId ?? dynRow?.id ?? null;
      return {
        bookArgs: {
          dynamicId,
          clientsInfo: clientsInfoFromFlightLead(
            passengers,
            mergedRoomsAndPaxForSupplements
          ),
          hotel: null,
          hotelOffer: null,
          flights: outbound.flightsInfo,
          agentID: agentId,
          total_price: totalPriceToSave,
          currency: "ILS",
          check_in: outbound.flightsInfo.dep_date,
          check_out:
            builtLegs[1]?.flightsInfo.dep_date || outbound.flightsInfo.ret_date,
        },
      };
    },
    [
      selectedFlightOffer,
      savedDeal,
      currencyRate.USD,
      getBaggageFeeFlat,
      calculateLDSDealFareForRoomsComposition,
      applyAgentFeeToOurPrice,
      roomsAndPaxInfo?.length,
      extras,
      extendedDealApiData,
      dynamicOrder,
      ourSavedLDSOrder,
      params.dynamicOrderId,
      passengers,
    ]
  );

  const refreshDynamicOrderState = useCallback(
    async (orderIdOverride = null) => {
      const rowId = orderIdOverride || pendingPaymentOrderId;
      if (!rowId) return null;
      const user = getUserDetails();
      const agentId = user?.id;
      if (!agentId) return null;
      const res = await getDynamicOrder({
        id: rowId,
        agentID: agentId,
        isAdmin: isAdminUser() ? 1 : 0,
      });
      const row = res?.data;
      if (row?.id) {
        setOurSavedLDSOrder(row);
        setDynamicOrder(row);
        if (row.paid === true || row.paid === "true" || row.paid === 1) {
          setPendingPaymentOrderId(null);
          setManualPaymentUrl("");
        }
      }
      return row || null;
    },
    [
      pendingPaymentOrderId,
      setOurSavedLDSOrder,
      setDynamicOrder,
    ]
  );

  useEffect(() => {
    if (!pendingPaymentOrderId) return undefined;
    let cancelled = false;
    const onFocus = () => {
      refreshDynamicOrderState(pendingPaymentOrderId).catch(() => {
        /* best effort */
      });
    };
    const intervalId = window.setInterval(() => {
      if (cancelled) return;
      refreshDynamicOrderState(pendingPaymentOrderId).catch(() => {
        /* best effort */
      });
    }, 8000);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.clearInterval(intervalId);
    };
  }, [pendingPaymentOrderId, refreshDynamicOrderState]);

  const submitProposalFromReview = useCallback(async () => {
    if (!useRealFlow) {
      if (canAdvanceFromCurrentStep()) nextStep();
      return;
    }

    if (currentStep !== "review") {
      if (canAdvanceFromCurrentStep()) nextStep();
      return;
    }

    if (
      !passengersStepValid ||
      !Array.isArray(passengers) ||
      passengers.length === 0
    ) {
      notification.error({
        message: "לא ניתן לשלוח",
        description: "יש להשלים ולוודא פרטי נוסעים בשלב הנוסעים.",
        placement: "bottomRight",
        duration: 5,
      });
      return;
    }

    const rawDeal = selectedFlightOffer?.rawDeal ?? savedDeal;
    if (!rawDeal) {
      notification.error({
        message: "לא ניתן לשלוח",
        description: "לא נבחרה טיסה לשמירה.",
        placement: "bottomRight",
        duration: 5,
      });
      return;
    }

    const dynRow = dynamicOrder || ourSavedLDSOrder;

    const user = getUserDetails();
    const agentId = user?.id;
    if (!agentId) {
      notification.error({
        message: "לא ניתן לשלוח",
        description: "אין מזהה סוכן — התחברו מחדש.",
        placement: "bottomRight",
        duration: 5,
      });
      return;
    }

    const ldsOrderId =
      dynRow?.ldsorderid ?? dynRow?.ldsOrderId ?? dynRow?.lds_order_id;
    const canRetryExistingLds =
      Boolean(ldsOrderId) && isFlightsNewProposalPackage(dynRow);

    let ourPrice = null;
    if (!canRetryExistingLds) {
      if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) {
        notification.error({
          message: "לא ניתן לשלוח",
          description:
            "חסר מחירון חדרים לטיסה — חזרו לתוצאות ובחרו הצעה מחדש.",
          placement: "bottomRight",
          duration: 6,
        });
        return;
      }

      const basePrice = calculateLDSDealFareForRoomsComposition(rawDeal);
      ourPrice = applyAgentFeeToOurPrice(basePrice, roomsAndPaxInfo?.length);
      if (!ourPrice?.ourTotalFare) {
        notification.error({
          message: "לא ניתן לשלוח",
          description: "לא ניתן לחשב מחיר להזמנה — נסו חיפוש מחדש.",
          placement: "bottomRight",
          duration: 5,
        });
        return;
      }
    }

    setIsSubmitting(true);
    setLastError(null);

    try {
      if (canRetryExistingLds) {
        const profileId = dynRow.id ?? params.dynamicOrderId ?? null;
        if (!profileId) {
          throw new Error("חסר מזהה חבילה — לא ניתן לשלוח לספק.");
        }
        const sendRes = await sendFlightProposalToSupplier(profileId, agentId, {
          isAdmin: isAdminUser(),
        });
        if (!sendRes?.success) {
          throw new Error(
            sendRes?.message_he ||
              sendRes?.error ||
              sendRes?.reason ||
              "שליחת הבקשה לספק נכשלה"
          );
        }
        notification.success({
          message: "ההזמנה הועברה לספק",
          description: "מעבירים לסיכום ההזמנה.",
          placement: "bottomRight",
          duration: 5,
        });
        navigate(`/p/dashboard/packages/proposal/${profileId}`);
        return;
      }

      const merged = mergeBookingFlowPassengersIntoRoomsAndPaxInfo(
        roomsAndPaxInfo,
        roomInfo,
        passengers
      );
      const passengersPayload = resolveIncomingPassengersForPayment({
        roomsAndPaxInfo: merged,
        roomInfo,
        passengers,
        passengersStepValid: true,
        existingPassengersInfo: existingPassengersInfoFromOrderRow(
          dynRow || ourSavedLDSOrder
        ),
      });
      const { bookArgs } = buildBookFlightOnlyPayload({
        agentId,
        passengersPayload,
        mergedRoomsAndPaxForSupplements: merged,
      });
      const response = await bookFlightOnly(bookArgs);

      let data = response?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (_) {
          data = null;
        }
      }
      if (data?.status !== "ok" || !data.id) {
        throw new Error(
          data?.message || data?.reason || data?.error || "שגיאה בשמירת ההצעה"
        );
      }

      const newId = data.id;
      setOurSavedLDSOrder(data);
      setDynamicOrder(data);

      const sendRes = await sendFlightProposalToSupplier(newId, agentId, {
        isAdmin: isAdminUser(),
      });
      if (!sendRes?.success) {
        throw new Error(
          sendRes?.message_he ||
            sendRes?.error ||
            sendRes?.reason ||
            "שליחת הבקשה לספק נכשלה"
        );
      }

      notification.success({
        message: "ההזמנה הועברה לספק",
        description: "מעבירים לסיכום ההזמנה.",
        placement: "bottomRight",
        duration: 5,
      });

      navigate(`/p/dashboard/packages/proposal/${newId}`);
    } catch (err) {
      let payload = err?.response?.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (_) {
          payload = null;
        }
      }
      const desc =
        (payload && (payload.reason || payload.error || payload.message)) ||
        err.message ||
        "שגיאה לא ידועה";
      setLastError(String(desc));
      notification.error({
        message: "שגיאה בשליחת הצעה לספק",
        description: String(desc),
        placement: "bottomRight",
        duration: 8,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    useRealFlow,
    currentStep,
    passengersStepValid,
    passengers,
    selectedFlightOffer,
    savedDeal,
    dynamicOrder,
    ourSavedLDSOrder,
    roomsAndPaxInfo,
    roomInfo,
    extras,
    calculateLDSDealFareForRoomsComposition,
    applyAgentFeeToOurPrice,
    buildBookFlightOnlyPayload,
    navigate,
    nextStep,
    canAdvanceFromCurrentStep,
    params.dynamicOrderId,
    setOurSavedLDSOrder,
    setDynamicOrder,
    notification,
  ]);

  const clearFlightQuoteCreateResult = useCallback(() => {
    setFlightQuoteCreateResult(null);
  }, []);

  const createDraftFromFlightDetails = useCallback(async (options = {}) => {
    if (draftInflightRef.current) return draftInflightRef.current;
    const { silent = false, skipQuoteResult = false } = options || {};
    if (!useRealFlow) return null;
    const rawDeal = selectedFlightOffer?.rawDeal ?? savedDeal;
    if (!rawDeal) {
      notification.error({
        message: "לא ניתן ליצור טיוטה",
        description: "לא נבחרה טיסה לשמירה.",
        placement: "bottomRight",
        duration: 5,
      });
      return null;
    }

    const user = getUserDetails();
    const agentId = user?.id;
    if (!agentId) {
      notification.error({
        message: "לא ניתן ליצור טיוטה",
        description: "אין מזהה סוכן — התחברו מחדש.",
        placement: "bottomRight",
        duration: 5,
      });
      return null;
    }

    const run = (async () => {
    setIsCreatingDraft(true);
    setLastError(null);
    setFlightQuoteCreateResult(null);
    try {
      const basePrice = calculateLDSDealFareForRoomsComposition(rawDeal);
      const ourPrice = applyAgentFeeToOurPrice(basePrice, roomsAndPaxInfo?.length);
      if (!ourPrice?.ourTotalFare) {
        throw new Error("לא ניתן לחשב מחיר להזמנה — נסו חיפוש מחדש.");
      }

      const dynRow = dynamicOrder || ourSavedLDSOrder;
      const mergedRoomsAndPax = Array.isArray(roomsAndPaxInfo)
        ? roomsAndPaxInfo
        : [];
      let mergedRoomsAndPaxForSupplements = mergedRoomsAndPax;

      if (
        Array.isArray(passengers) &&
        passengers.length > 0 &&
        mergedRoomsAndPax.length > 0 &&
        (passengersStepValid ||
          uiPassengersHaveCompleteNames(passengers) ||
          uiPassengersHaveAnyName(passengers))
      ) {
        mergedRoomsAndPaxForSupplements =
          mergeBookingFlowPassengersIntoRoomsAndPaxInfo(
            mergedRoomsAndPax,
            roomInfo,
            passengers
          );
      }
      const passengersPayload = resolveIncomingPassengersForPayment({
        roomsAndPaxInfo: mergedRoomsAndPaxForSupplements,
        roomInfo,
        passengers,
        passengersStepValid,
        existingPassengersInfo: existingPassengersInfoFromOrderRow(dynRow),
      });

      const { bookArgs } = buildBookFlightOnlyPayload({
        agentId,
        passengersPayload,
        mergedRoomsAndPaxForSupplements,
      });
      const response = await bookFlightOnly(bookArgs);

      let data = response?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (_) {
          data = null;
        }
      }
      if (data?.status !== "ok" || !data?.id) {
        throw new Error(
          data?.message || data?.reason || data?.error || "שגיאה בשמירת הטיוטה"
        );
      }

      const draftId = data.draft_id ?? data.id;
      const publicBase = resolvePublicBaseUrl({ skipTrace: true });
      let shareUrl =
        data.public_offer_link ||
        data.public_share_url ||
        buildPublicFlightOrDynamicOfferUrl(publicBase, draftId, { flightOnly: true });
      shareUrl = absolutizePublicFrontendUrl(String(shareUrl || "").trim());

      const resolvedPublicUrl = String(shareUrl || "").trim() || null;

      setOurSavedLDSOrder(data);
      setDynamicOrder(data);
      if (!skipQuoteResult) {
        setFlightQuoteCreateResult({
          offerId: String(draftId),
          publicOfferUrl: resolvedPublicUrl,
          draftStatus: data.draft_status || "draft",
        });
      }
      if (!silent) {
        notification.success({
          message: "ההצעה נוצרה",
          description: "אפשר להעתיק את קישור הצפייה ללקוח או לפתוח את ההצעה — ללא מעבר מסך.",
          placement: "bottomRight",
          duration: 3.5,
        });
      }
      window.dispatchEvent(new Event("ldsDraftsChanged"));
      return {
        draftId,
        shareUrl,
        status: data.draft_status || "draft",
        orderData: data,
      };
    } catch (err) {
      const desc =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "שגיאה לא ידועה";
      setLastError(String(desc));
      notification.error({
        message: "שגיאה ביצירת טיוטה",
        description: String(desc),
        placement: "bottomRight",
        duration: 8,
      });
      return null;
    } finally {
      setIsCreatingDraft(false);
    }
    })();
    draftInflightRef.current = run;
    try {
      return await run;
    } finally {
      if (draftInflightRef.current === run) draftInflightRef.current = null;
    }
  }, [
    useRealFlow,
    selectedFlightOffer,
    savedDeal,
    notification,
    calculateLDSDealFareForRoomsComposition,
    applyAgentFeeToOurPrice,
    buildBookFlightOnlyPayload,
    roomsAndPaxInfo,
    passengersStepValid,
    passengers,
    roomInfo,
    extras,
    dynamicOrder,
    ourSavedLDSOrder,
    params.dynamicOrderId,
    setOurSavedLDSOrder,
    setDynamicOrder,
  ]);

  const ensureOrderForPayment = useCallback(async (options = {}) => {
    const skipPriceRefresh = Boolean(options?.skipPriceRefresh);
    const existingId = resolveExistingDynamicOrderId();

    if (existingId) {
      const latestRes = await getDynamicOrder({
        id: existingId,
        agentID: getUserDetails()?.id,
        isAdmin: isAdminUser() ? 1 : 0,
      });
      const latestOrder = latestRes?.data?.id ? latestRes.data : dynamicOrder || ourSavedLDSOrder || null;
      const serverTotal = Number(latestOrder?.total_price || 0);
      // Use BookingFlow SSOT total first (includes selected baggage/supplements).
      const uiTotal = Number(
        pricing?.total ??
          pricing?.managedOurTotalFare?.priceWithCommissionAndCreditCardFee ??
          0
      );
      const diff = Math.abs(uiTotal - serverTotal);
      const priceDiffers = Number.isFinite(diff) && diff > 1;
      const existingPassengersInfo = existingPassengersInfoFromOrderRow(latestOrder);
      const incomingPassengersInfo = resolveIncomingPassengersForPayment({
        roomsAndPaxInfo,
        roomInfo,
        passengers,
        passengersStepValid,
        existingPassengersInfo,
      });
      const passengersNeedSync = flightPassengersInfoNeedsSync(
        existingPassengersInfo,
        incomingPassengersInfo
      );
      const persistAction = resolveFlightPaymentPersistAction({
        hasExistingOrder: true,
        priceDiffers,
        passengersNeedSync,
        skipPriceRefresh,
      });
      const ldsOrderId = resolveExistingLdsOrderIdFromDynamicRow(latestOrder);
      logFlightPassengerSyncTrace({
        ...summarizeFlightPassengersForTrace({
          orderId: ldsOrderId || existingId,
          passengersInfo: incomingPassengersInfo,
        }),
        persist_action: persistAction,
      });

      assertFlightPassengersHaveNames(incomingPassengersInfo, passengers);

      if (persistAction === "refresh_price_and_passengers") {
        const refreshed = await createDraftFromFlightDetails({
          silent: true,
          skipQuoteResult: true,
        });
        const refreshedOrderData = refreshed?.orderData || null;
        return {
          orderId: String(refreshed?.draftId || existingId),
          orderData: refreshedOrderData || latestOrder,
        };
      }

      if (persistAction === "sync_passengers") {
        if (!ldsOrderId) {
          throw new Error("לא נמצאה הזמנת ספק לעדכון שמות נוסעים");
        }
        const syncRes = await updateLdsOrder(
          buildFlightPassengersInfoOnlyUpdatePayload({
            ldsOrderId,
            passengersInfo: incomingPassengersInfo,
          })
        );
        const syncPayload = syncRes?.data;
        if (
          syncPayload &&
          syncPayload.status &&
          String(syncPayload.status).toLowerCase() !== "ok"
        ) {
          throw new Error(
            syncPayload.message || syncPayload.error || "עדכון שמות נוסעים נכשל"
          );
        }
        const refreshedRes = await getDynamicOrder({
          id: existingId,
          agentID: getUserDetails()?.id,
          isAdmin: isAdminUser() ? 1 : 0,
        });
        return {
          orderId: existingId,
          orderData: refreshedRes?.data?.id ? refreshedRes.data : latestOrder,
        };
      }

      return {
        orderId: existingId,
        orderData: latestOrder,
      };
    }
    assertFlightPassengersHaveNames(
      resolveIncomingPassengersForPayment({
        roomsAndPaxInfo,
        roomInfo,
        passengers,
        passengersStepValid,
        existingPassengersInfo: existingPassengersInfoFromOrderRow(
          dynamicOrder || ourSavedLDSOrder
        ),
      }),
      passengers
    );
    const created = await createDraftFromFlightDetails({
      silent: true,
      skipQuoteResult: true,
    });
    if (!created?.draftId) {
      throw new Error("שמירת ההזמנה נכשלה");
    }
    return {
      orderId: String(created.draftId),
      orderData: created.orderData || null,
    };
  }, [
    resolveExistingDynamicOrderId,
    createDraftFromFlightDetails,
    dynamicOrder,
    ourSavedLDSOrder,
    extras,
    roomsAndPaxInfo,
    roomInfo,
    passengers,
    passengersStepValid,
    pricing?.total,
    pricing?.managedOurTotalFare?.priceWithCommissionAndCreditCardFee,
  ]);

  const submitPayNowFromReview = useCallback(async () => {
    if (
      !useRealFlow ||
      (currentStep !== "review" && currentStep !== "draftPayment")
    ) {
      return;
    }
    setIsSubmittingPayNow(true);
    setLastError(null);
    try {
      const user = getUserDetails();
      const agentId = user?.id;
      if (!agentId) {
        throw new Error("אין מזהה סוכן — התחברו מחדש.");
      }
      const { orderId, orderData } = await ensureOrderForPayment();
      const freshOrderRes = await getDynamicOrder({
        id: orderId,
        agentID: agentId,
        isAdmin: isAdminUser() ? 1 : 0,
      });
      const canonicalOrder = freshOrderRes?.data?.id ? freshOrderRes.data : orderData;
      const flightsRow = canonicalOrder?.flights || {};
      const our =
        (flightsRow?.ourPrice && typeof flightsRow.ourPrice === "object" ? flightsRow.ourPrice : null) ||
        (flightsRow?.ourprice && typeof flightsRow.ourprice === "object" ? flightsRow.ourprice : null) ||
        {};
      const baseManagedFinal = Number(resolveLdsManagedPayableBaseIls(our) || 0);
      let checkedSupplementsTotal = 0;
      const rawSupplements = flightsRow?.supplement;
      if (rawSupplements && typeof rawSupplements === "object") {
        const suppEntries = Array.isArray(rawSupplements)
          ? rawSupplements
          : Object.values(rawSupplements);
        checkedSupplementsTotal = suppEntries.reduce((sum, entry) => {
          if (!entry || typeof entry !== "object" || entry.checked !== true) return sum;
          const fare = Number(entry.ourFare ?? 0);
          return sum + (Number.isFinite(fare) ? fare : 0);
        }, 0);
      }
      const amountRaw =
        baseManagedFinal > 0
          ? baseManagedFinal + checkedSupplementsTotal
          : canonicalOrder?.total_price ??
            canonicalOrder?.flights?.ourPrice?.ourTotalFare?.priceWithCommissionAndCreditCardFee ??
            canonicalOrder?.flights?.ourprice?.ourTotalFare?.priceWithCommissionAndCreditCardFee ??
            0;
      const amount = round2(Number(amountRaw) || 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error("לא ניתן ליצור קישור תשלום — סכום לא תקין.");
      }
      const paymentRes = await axios.post(
        `${ApiReqBaseUrl}/getDynamicPaymentUrl`,
        {
          amount,
          actualAmount: amount,
          currency: "ILS",
          orderId,
          tash: 1,
          agentId,
          description: `תשלום עבור הזמנת טיסות ${orderId}`,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          validateStatus: () => true,
        }
      );
      const payload = paymentRes?.data;
      if (!payload || payload.status !== "ok") {
        throw new Error(
          payload?.message || payload?.error || "יצירת קישור תשלום נכשלה"
        );
      }
      const updatedOrder = payload?.order;
      if (updatedOrder?.id) {
        setOurSavedLDSOrder(updatedOrder);
        setDynamicOrder(updatedOrder);
      }
      const unpaidPayments = Array.isArray(updatedOrder?.payments)
        ? updatedOrder.payments.filter((p) => !p?.paid)
        : [];
      const paymentUrl =
        unpaidPayments?.[0]?.paymentUrl ||
        unpaidPayments?.[0]?.url ||
        updatedOrder?.paymentUrl ||
        updatedOrder?.url ||
        payload?.paymentUrl ||
        "";
      if (!paymentUrl) {
        throw new Error("לא התקבל קישור תשלום תקין.");
      }
      setPendingPaymentOrderId(String(orderId));
      setManualPaymentUrl("");
      const popup = window.open(paymentUrl, "_blank", "noopener,noreferrer");
      const popupFeatures = [
        "popup=yes",
        "width=1200",
        "height=850",
        `left=${Math.max(0, Math.round((window.screen.width - 1200) / 2))}`,
        `top=${Math.max(0, Math.round((window.screen.height - 850) / 2))}`,
        "resizable=yes",
        "scrollbars=yes",
      ].join(",");
      const popupFallback =
        popup || window.open(paymentUrl, "hypPaymentWindow", popupFeatures);
      if (!popupFallback) {
        setManualPaymentUrl(paymentUrl);
        notification.info({
          message: "חלון קופץ נחסם",
          description: "לא ניתן לפתוח חלון אוטומטית. השתמשו בקישור 'פתח עמוד תשלום'.",
          placement: "bottomRight",
          duration: 4,
        });
      } else {
        notification.success({
          message: "מעבירים לתשלום",
          description: "נפתח חלון תשלום מאובטח.",
          placement: "bottomRight",
          duration: 4,
        });
      }
    } catch (err) {
      const namesToast = isFlightPassengerNamesIncompleteError(err)
        ? resolveBankTransferFailureToast(err)
        : null;
      const desc =
        namesToast?.description ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "שגיאה לא ידועה";
      setLastError(String(desc));
      notification.error({
        message: namesToast?.message || "שגיאה בהעברה לתשלום",
        description: String(desc),
        placement: "bottomRight",
        duration: 8,
      });
    } finally {
      setIsSubmittingPayNow(false);
    }
  }, [
    useRealFlow,
    currentStep,
    ensureOrderForPayment,
    pricing?.total,
    notification,
    setOurSavedLDSOrder,
    setDynamicOrder,
  ]);

  const submitBankTransferFromReview = useCallback(async () => {
    if (
      !useRealFlow ||
      (currentStep !== "review" && currentStep !== "draftPayment")
    ) {
      return;
    }
    setIsSubmittingBankTransfer(true);
    setLastError(null);
    try {
      const user = getUserDetails();
      const agentId = user?.id;
      if (!agentId) {
        throw new Error("אין מזהה סוכן — התחברו מחדש.");
      }
      let { orderId } = await ensureOrderForPayment({
        skipPriceRefresh: true,
      });
      const fetchFresh = async (id) => {
        const freshOrderRes = await getDynamicOrder({
          id,
          agentID: agentId,
          isAdmin: isAdminUser() ? 1 : 0,
        });
        return freshOrderRes?.data?.id ? freshOrderRes.data : null;
      };
      const uiTotal = Number(
        pricing?.total ??
          pricing?.managedOurTotalFare?.priceWithCommissionAndCreditCardFee ??
          0
      );
      let fresh = await fetchFresh(orderId);
      if (shouldRefreshDraftBeforeBankTransfer({ orderRow: fresh, uiTotalIls: uiTotal })) {
        const refreshed = await createDraftFromFlightDetails({
          silent: true,
          skipQuoteResult: true,
        });
        if (refreshed?.draftId) {
          orderId = String(refreshed.draftId);
        }
      }
      let res = await postFlightsNewProposalBankTransfer({
        dynamicOrderId: orderId,
        agentID: agentId,
      });
      if (shouldRetryBankTransferAfterFailure(res?.data)) {
        const refreshed = await createDraftFromFlightDetails({
          silent: true,
          skipQuoteResult: true,
        });
        if (refreshed?.draftId) {
          orderId = String(refreshed.draftId);
        }
        res = await postFlightsNewProposalBankTransfer({
          dynamicOrderId: orderId,
          agentID: agentId,
        });
      }
      const payload = res?.data;
      if (!payload || payload.status !== "ok") {
        throw new Error(
          payload?.message_he ||
            payload?.message ||
            payload?.error ||
            "שמירת בקשת העברה נכשלה"
        );
      }
      if (payload?.order?.id) {
        setOurSavedLDSOrder(payload.order);
        setDynamicOrder(payload.order);
      }
      notification.success({
        message: "הבקשה נשמרה בהעברה בנקאית",
        description: "ההזמנה הועברה לטיפול ותאושר לאחר קליטת התשלום.",
        placement: "bottomRight",
        duration: 5,
      });
      const routeBase = location.pathname.startsWith("/p/dashboard")
        ? "/p/dashboard/dynamic/success/"
        : "/dynamic/success/";
      navigate(`${routeBase}${orderId}`);
    } catch (err) {
      const toast = resolveBankTransferFailureToast(err);
      const desc = toast?.description || "שגיאה לא ידועה";
      setLastError(String(desc));
      if (toast) {
        notification.error({
          message: toast.message,
          description: String(desc),
          placement: "bottomRight",
          duration: 8,
        });
      }
    } finally {
      setIsSubmittingBankTransfer(false);
    }
  }, [
    useRealFlow,
    currentStep,
    ensureOrderForPayment,
    createDraftFromFlightDetails,
    pricing?.total,
    pricing?.managedOurTotalFare?.priceWithCommissionAndCreditCardFee,
    notification,
    setOurSavedLDSOrder,
    setDynamicOrder,
    location.pathname,
    navigate,
  ]);

  const value = useMemo(
    () => ({
      useRealFlow,
      submitProposalFromReview,
      isSubmittingProposal: isSubmitting,
      createDraftFromFlightDetails,
      isCreatingDraftFromFlightDetails: isCreatingDraft,
      ensureOrderForPayment,
      submitPayNowFromReview,
      isSubmittingPayNow,
      submitBankTransferFromReview,
      isSubmittingBankTransfer,
      manualPaymentUrl,
      clearManualPaymentUrl: () => setManualPaymentUrl(""),
      proposalSubmitError: lastError,
      clearProposalSubmitError: () => setLastError(null),
      flightQuoteCreateResult,
      clearFlightQuoteCreateResult,
    }),
    [
      useRealFlow,
      submitProposalFromReview,
      isSubmitting,
      createDraftFromFlightDetails,
      isCreatingDraft,
      ensureOrderForPayment,
      submitPayNowFromReview,
      isSubmittingPayNow,
      submitBankTransferFromReview,
      isSubmittingBankTransfer,
      manualPaymentUrl,
      lastError,
      flightQuoteCreateResult,
      clearFlightQuoteCreateResult,
    ]
  );

  return (
    <FlightRealProposalSubmitContext.Provider value={value}>
      {children}
    </FlightRealProposalSubmitContext.Provider>
  );
}

export function useFlightRealProposalSubmit() {
  const ctx = useContext(FlightRealProposalSubmitContext);
  if (!ctx) {
    return {
      useRealFlow: false,
      submitProposalFromReview: async () => {},
      isSubmittingProposal: false,
      createDraftFromFlightDetails: async () => null,
      isCreatingDraftFromFlightDetails: false,
      ensureOrderForPayment: async () => null,
      submitPayNowFromReview: async () => null,
      isSubmittingPayNow: false,
      submitBankTransferFromReview: async () => null,
      isSubmittingBankTransfer: false,
      manualPaymentUrl: "",
      clearManualPaymentUrl: () => {},
      proposalSubmitError: null,
      clearProposalSubmitError: () => {},
      flightQuoteCreateResult: null,
      clearFlightQuoteCreateResult: () => {},
    };
  }
  return ctx;
}
